import { addNotification, addPageError } from '../stores'
import { redirectToLoginWithReturn } from './errorRedirect'
import { DefaultNotification, NotificationType } from '$types/notification'
import { reportException } from './reportException'

export const fetchUtils = {
	get,
	post,
	put,
	delete: _delete,
	showError,
	catchErrorAndShowNotification,
	postAndDownload
}

export type FetchError = {
	code: number
	message: string
	data: object
	/**
	 * true when doHandle already handled the error terminally (401 login
	 * redirect, or 403/404/410 via addPageError). Single-owner rule: a page
	 * .catch() may only run its own fallback (e.g. redirectToNotFoundRoute)
	 * when this is false — acting twice renders one error state over another
	 * URL (403 page with /404 in the URL).
	 */
	handled?: boolean
}

function showError(page, customMessage) {
	return (error) => {
		console.error(error)
		page.showErrorMsg(customMessage ?? error.message ?? error)
	}
}

function get(url: string, publishPageError = true) {
	const requestOptions = {
		method: 'GET'
	}
	return fetch(url, requestOptions).then(doHandle(publishPageError))
}

function post(url: string, body, publishPageError = true, stringify = true) {
	const bodyData = stringify ? JSON.stringify(body) : body
	const requestOptions = {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: bodyData
	}
	return fetch(url, requestOptions).then(doHandle(publishPageError))
}

async function postAndDownload(
	url: string,
	body: any,
	defaultFilename: string,
	publishPageError = true,
	stringify = true
) {
	const bodyData = stringify ? JSON.stringify(body) : body
	const requestOptions = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: bodyData
	}
	const response = await fetch(url, requestOptions)
	if (response.status === 200 || response.ok) {
		const blob = await response.blob()
		_downloadBlob(blob, _getFilenameFromResponse(response) || defaultFilename)
		return Promise.resolve()
	} else {
		const json = await response.json()
		const error = json?.errorMessage || response.statusText
		const currentError = {
			code: response.status,
			message: error,
			data: json.data
		} as FetchError
		if (response.status in SpecialHandledErrors && publishPageError) {
			addPageError(currentError)
		}
		return Promise.reject(currentError)
	}
}

function put(url: string, body, publishPageError = true) {
	const requestOptions = {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body)
	}
	return fetch(url, requestOptions).then(doHandle(publishPageError))
}

const CONTENT_DISPOSITION_FILENAME_REGEX = /filename="(.+)"/

// prefixed with underscored because delete is a reserved word in javascript
function _delete(url: string, publishPageError = true) {
	const requestOptions = {
		method: 'DELETE'
	}
	return fetch(url, requestOptions).then(doHandle(publishPageError))
}

function _downloadBlob(blob, filename) {
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = filename
	a.click()
	URL.revokeObjectURL(url)
}

function _getFilenameFromResponse(response) {
	const contentDisposition = response.headers.get('Content-Disposition')
	if (contentDisposition) {
		const match = contentDisposition.match(CONTENT_DISPOSITION_FILENAME_REGEX)
		return match ? match[1] : null
	}
	return null
}

function catchErrorAndShowNotification(target: string | null = null) {
	return (error) => {
		const notification = DefaultNotification(
			error.message ?? error,
			NotificationType.ERROR,
			true,
			0,
			target
		)
		addNotification(notification)
	}
}

export enum SpecialHandledErrors {
	NOT_AUTHORIZED = 401,
	MISSING_PERMISSION = 403,
	NOT_FOUND = 404,
	GONE = 410
}

/**
 * sirius masks not-logged-in as HTTP 200 { success: false, error: true,
 * message: "An error occurred: Missing permission: flag-logged-in" } — the
 * status code carries no auth signal, only this message does (measured, see
 * REPORT.md). String-matching it here mirrors what the backend's own
 * ApiController.onApiError does; a typed signal would need a backend change.
 */
const NOT_LOGGED_IN_MARKER = 'Missing permission: flag-logged-in'

/** envelope = { success, errorCode, ... }; raw payloads (arrays, plain data) are not */
function isEnvelope(json: unknown): json is JSONResponse {
	return typeof json === 'object' && json !== null && 'success' in json
}

function responseMeta(response: Response) {
	return {
		status: response.status,
		statusText: response.statusText,
		contentType: response.headers.get('content-type'),
		url: response.url
	}
}

// helper functions
export function doHandle(publishPageError: boolean) {
	return async function (response: Response): Promise<any> {
		let json: JSONResponse | null = null
		let parseFailed = false
		try {
			json = await response.json()
		} catch (parseError) {
			// non-JSON body (proxy "Bad Gateway" text, HTML error page):
			// fall through to the error path with the HTTP status alone
			parseFailed = true
			reportException(
				parseError instanceof Error ? parseError : new Error('doHandle: Non-JSON response'),
				{
					extra: {
						...responseMeta(response),
						parseError: String(parseError)
					}
				}
			)
		}

		const envelopeError = isEnvelope(json) && !json.success
		if (!response.ok || parseFailed || envelopeError) {
			const message =
				json?.errorMessage || json?.message || response.statusText || `HTTP ${response.status}`
			let code = response.status
			if (response.ok) {
				// HTTP status says 200 — the envelope is the only truth we have
				code =
					json?.errorCode ||
					(message.includes(NOT_LOGGED_IN_MARKER)
						? SpecialHandledErrors.NOT_AUTHORIZED
						: response.status)
			}
			const currentError: FetchError = {
				code,
				message,
				data: json?.data,
				handled: false
			}
			if (code in SpecialHandledErrors && publishPageError) {
				currentError.handled = true
				if (code === SpecialHandledErrors.NOT_AUTHORIZED) {
					// 401 = not authenticated: go log in (with return URL), never an
					// in-place error page with permissions wording
					redirectToLoginWithReturn()
				} else {
					addPageError(currentError)
					reportException(new Error('doHandle: Fetch error'), {
						extra: {
							...responseMeta(response),
							currentError
						}
					})
				}
			}
			console.error(currentError)
			return Promise.reject(currentError)
		}

		// ?? (nullish coalescing — only falls back on null/undefined, not false)
		// -> data is not enveloped with success, errorCode, etc.
		return json?.data ?? json
	}
}

type JSONResponse = {
	data: any
	errorCode: number
	hasValidationErrors: boolean
	success: boolean
	successCode: any
	validationErrors: any[]
	errorMessage?: string
	/** flat sirius error shape ({ success:false, error:true, message }) — no errorCode/errorMessage */
	message?: string
}
